import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation, withTranslation } from 'react-i18next';
import { FlightsManagerDesktop } from '../FlightsManager';
import { DynpackManagerDesktop } from '../DynpackManager';
import { PrimeBanner } from '../PrimeBanner';
import { Tab } from './Tab';
import { IframeProductManager } from './IframeProductManager';
import {
    Config,
    ABUtils,
    ErrorBoundary,
    TrackingUtils,
    setParams,
    withPageContext,
    BuyPathContext,
    isRailNewBubbleMarket,
} from '../../utils';
import {
    BuyPath,
    EntryPointProp,
    EntryPointPropDefault,
    TabType,
    TabTypeProp,
    TabTypePropDefault,
    TripType,
    TripTypeProp,
    TripTypePropDefault,
    SearchSegment,
    EntryPoint,
    searcherConfigs,
} from '../../models';
import { setActiveProduct } from '../../redux/activeProduct/actions';
import {
    setHotel,
    setTripType,
    setSeparateStay,
    updateSegment,
    updateSearcherConfig,
    showErrors,
} from '../../redux/currentSearch/actions';
import IframeListener from './IframeListener';
import DiscountBannerForPrimePortal from '../DiscountBanners/DiscountBannerForPrimePortal';
import { PrimeBenefitBanner } from '../PrimeBenefitBanner';
import { HotelTabManager } from '../HotelManager';
import { isLoggedIn } from '../../redux/userAuthentication/actions';
import { userAuthenticationSelector } from '../../redux/userAuthentication/selectors';
import { propagateLoginStatusToOdigeoJS } from '../../utils/OdigeoInteractions';

const carsIframeListener = () => {
    TrackingUtils.trackEvent({ action: 'searcher_cars', label: 'clicked_cars_iframe' });
};

const setActiveProductIfWMP = ({ wmpProductList, setActiveProduct, selectedTab }) => {
    if (wmpProductList.length && (selectedTab === EntryPoint.HOTELS || selectedTab === EntryPoint.TRAINS)) {
        setActiveProduct(selectedTab);
    }
};

const getInitialSelectedTab = ({ activeTab, rootDataAttr }) =>{
    return rootDataAttr?.activetab ? rootDataAttr.activetab : activeTab;
};

const iframeContextFactory = ( { isPrimeEnabled, isPrimeUser, isPrimeCookieActive, isUserLoggedIn } ) => {
    return {
        isPrimeBenefits: isPrimeEnabled && isPrimeUser,
        isPrimeBanner: isPrimeEnabled && !isUserLoggedIn && !isPrimeUser,
        isUserMember: isPrimeUser,
        isPrimeCookieActive,
        currencyCode: Config.get('i18n').currency.currencyCode,
    };
};

export const TabManager = (props) => {
    const {
        usersResponse,
        initial: initialUsersResponse,
        isFetching: isFetchingUsersResponse,
    } = useSelector((state) => userAuthenticationSelector(state));
    const isUserLoggedIn = Object.keys(usersResponse).length !== 0 && usersResponse?.userDTO !== null;
    const isPrimeUser = useCallback(() => usersResponse.membership ?? false, [usersResponse.membership]);
    const [selectedTab, setSelectedTab] = useState(getInitialSelectedTab({
        activeTab: props.activeTab,
        rootDataAttr: props.rootDataAttr,
    }));
    const [url, setUrl] = useState('');
    const [carUrl, setCarUrl] = useState('');

    const showCarWidget = Config.get('isShowCarWidget') && isPrimeUser();
    const isPrimeEnabled = Config.get('isPrimeEnabled');
    const isHotelsForPrime = Config.get('isHotelsForPrime');
    const isCarsForPrime = Config.get('isCarsForPrime');
    const isRailNewBubbleEnabled = isRailNewBubbleMarket();

    const { t } = useTranslation();
    const previousId = useRef();
    const dispatch = useDispatch();

    const iframeContext = iframeContextFactory({
        isPrimeEnabled,
        isPrimeUser: isPrimeUser(),
        isPrimeCookieActive: isPrimeUser(),
        isUserLoggedIn
    });

    useEffect(() => {
        dispatch(isLoggedIn());
    }, [dispatch]);

    useEffect(() => {
        !initialUsersResponse && !isFetchingUsersResponse && propagateLoginStatusToOdigeoJS(isUserLoggedIn, usersResponse);
    }, [initialUsersResponse, isFetchingUsersResponse, isUserLoggedIn, usersResponse]);

    useEffect(() => {
        async function setNewUrl() {
            const key =`home.services.${selectedTab}.url`;
            let src;
            if (selectedTab === TabType.HOTELS) {
                src = setParams(t(key), { selected_currency: iframeContext.currencyCode });
            } else if (selectedTab === TabType.VP) {
                src = t('home.services.holidaypackages.url');
            } else if (selectedTab === TabType.CARS) {
                const k = isPrimeUser() && isCarsForPrime
                    ? `home.services.${TabType.CARS}.cartrawler.url`
                    : `home.services.${TabType.CARS}.url`;
                src = setParams(t(k), { curr: iframeContext.currencyCode });
            } else {
                src = t(key);
            }
            const newUrl = await TrackingUtils.addCrossDomainTracking(src);
            setUrl(newUrl);
            if (selectedTab === TabType.CARS) {
                setCarUrl(newUrl);
            }
        }
        setNewUrl();
    }, [iframeContext.currencyCode, isPrimeUser, isCarsForPrime, selectedTab, t]);

    const getProductList = () => {
        const defaultProducts = Config.get('products');
        const productsFromWMP = props.wmpProductList.length > 0 ?
            props.wmpProductList.split(',').filter((item) => defaultProducts.includes(item)) : [];
        const products = productsFromWMP.length > 0 ? productsFromWMP : defaultProducts;

        return products;
    };

    const tabIds = getProductList();
    const hasHotel = tabIds.includes(TabType.DP);

    tabIds.indexOf(selectedTab) === -1 && props.setActiveProduct(tabIds[0]);

    const allTabs = useMemo(() => {
        const { entryPoint } = props;
        const { rootDataAttr } = props;
        const { isAbTrain } = ABUtils.getABs();
        const isWMP = Config.get('isWMP');

        return [
            {
                id: TabType.FLIGHTS,
                icon: 'odf-icon-flight',
                textKey: isAbTrain ? 'home.services.flights.TRAINS' : 'home.services.flights.main',
                render: () => (
                    <ErrorBoundary key={TabType.FLIGHTS}>
                        {showCarWidget && <DiscountBannerForPrimePortal />}
                        <BuyPathContext.Provider
                            value={{
                                FLIGHTS: (isWMP && (entryPoint === EntryPoint.HOME || entryPoint === EntryPoint.FLIGHTS))
                                    ? BuyPath.HOME_PAGE_WMP_FLIGHTS_TAB : BuyPath.FLIGHTS_PAGE,
                                DP: BuyPath.getDP(entryPoint, false, isWMP),
                            }}
                        >
                            <FlightsManagerDesktop isHotelOptionEnabled={hasHotel} rootDataAttr={rootDataAttr} isRailsSearcher={false} />
                        </BuyPathContext.Provider>
                    </ErrorBoundary>
                ),
            },
            {
                id: TabType.HOTELS,
                icon: 'odf-icon-hotel',
                render: () => <HotelTabManager rootDataAttr={rootDataAttr} />,
                textKey: 'home.services.hotels.text',
            },
            {
                id: TabType.DP,
                icon: isAbTrain ? 'odf-icon-baggage' : 'odf-icon-flight-hotel',
                textKey: isAbTrain ? 'home.services.flighthotel.TRAINS' : 'home.services.flighthotel.main',
                bubbleKey: !isRailNewBubbleEnabled ? 'home.services.bubble.dp' : '',
                render: () => (
                    <ErrorBoundary key={TabType.DP}>
                        {showCarWidget && <DiscountBannerForPrimePortal />}
                        <BuyPathContext.Provider
                            value={{
                                FLIGHTS: (isWMP && (entryPoint === EntryPoint.HOME || entryPoint === EntryPoint.FLIGHTS))
                                    ? BuyPath.HOME_PAGE_WMP_FLIGHTS_TAB : BuyPath.FLIGHTS_PAGE,
                                DP: BuyPath.getDP(entryPoint, true, isWMP),
                            }}
                        >
                            <DynpackManagerDesktop />
                        </BuyPathContext.Provider>
                    </ErrorBoundary>
                ),
            },
            {
                id: TabType.TRAINS,
                icon: 'odf-icon-train-right',
                textKey: 'home.services.flights.TRAINS',
                bubbleKey: isRailNewBubbleEnabled ? 'home.services.bubble.trains' : '',
                render: () => (
                    <ErrorBoundary key={TabType.TRAINS}>
                        {showCarWidget && <DiscountBannerForPrimePortal />}
                        <BuyPathContext.Provider
                            value={{
                                FLIGHTS: (isWMP && (entryPoint === EntryPoint.HOME || entryPoint === EntryPoint.FLIGHTS))
                                    ? BuyPath.HOME_PAGE_WMP_FLIGHTS_TAB : BuyPath.FLIGHTS_PAGE,
                                TRAINS: isWMP ? BuyPath.HOME_RAILS_WMP : BuyPath.HOME_RAIL_TAB,
                            }}
                        >
                            <FlightsManagerDesktop isHotelOptionEnabled={false} rootDataAttr={rootDataAttr} isRailsSearcher />
                        </BuyPathContext.Provider>
                    </ErrorBoundary>
                ),
            },
            {
                id: TabType.CARS,
                icon: 'odf-icon-car',
                textKey: 'home.services.cars.text',
                render: () => showCarWidget && <DiscountBannerForPrimePortal />,
            },
            {
                id: TabType.HOLIDAYSPACKAGES,
                icon: 'odf-icon-weather',
                textKey: 'home.services.holidaypackages.text',
                render: () => (
                    <ErrorBoundary key={TabType.HOLIDAYSPACKAGES}>
                        {showCarWidget && <DiscountBannerForPrimePortal />}
                        <IframeProductManager
                            product={TabType.HOLIDAYSPACKAGES}
                            height="311px"
                            appContext={iframeContext}
                            url={url}
                        />
                    </ErrorBoundary>
                ),
            },
            {
                id: TabType.VP,
                icon: 'odf-icon-weather',
                textKey: 'home.services.holidaypackages.text',
                render: () => (
                    <ErrorBoundary key={TabType.VP}>
                        {showCarWidget && <DiscountBannerForPrimePortal />}
                        <IframeProductManager
                            product={TabType.VP}
                            height="245px"
                            appContext={iframeContext}
                            url={url}
                        />
                    </ErrorBoundary>
                ),
            },
        ];
    }, [hasHotel, iframeContext, props, showCarWidget, url]);

    const getTabsToRender = () =>
        tabIds.map((tabId) => allTabs.find((tab) => tab.id === tabId));


    setActiveProductIfWMP({
        wmpProductList: props.wmpProductList,
        setActiveProduct: props.setActiveProduct,
        selectedTab,
    });

    const renderTabContent = () => {
        const tab = getTabsToRender().find((tab) => tab.id === selectedTab);
        return tab ? tab.render(url) : <React.Fragment />;
    };

    const hasToSanitizeSegments = (id) => {
        const { segments } = props;
        const destinationSegment = segments[0].destination || {};

        const isHtmlAutocompleteCaptions = destinationSegment.caption;
        const isLocationWithoutIATA = (id === TabType.FLIGHTS || id === TabType.DP) && !destinationSegment.iata;
        const isInspirationalLinkDP = id !== TabType.FLIGHTS && destinationSegment.geoNodeId === 0;
        const isGeoIdFromAnotherSearchform = destinationSegment.sourceProduct && destinationSegment.sourceProduct !== id;

        return isHtmlAutocompleteCaptions || isLocationWithoutIATA || isInspirationalLinkDP || isGeoIdFromAnotherSearchform;
    };

    const hideLoginWidgets = ( id ) => {
        const logginToggleContainer = document.getElementById('home_login_toggle');
        if ( logginToggleContainer ) {
            logginToggleContainer.style.display = (id !== TabType.HOTELS) ? 'none' : 'block';
        }
    };

    const hideDealsWidget = ( id ) => {
        const dealsContainer = document.getElementById('home_hottest_deals_container');
        if ( dealsContainer ) {
            dealsContainer.style.display = id !== TabType.FLIGHTS ? 'none' : 'block';
        }
    };

    const setRoundTripIfNeeded = (id, tripType, setTripType, updateSearcherConfig) => {
        if ((id === TabType.DP || id === TabType.HOTELS) && tripType !== TripType.ROUND_TRIP) {
            setTripType(TripType.ROUND_TRIP);
            updateSearcherConfig(searcherConfigs.default);
        }
    };

    const updateConfigForTrains = (id, tripType, updateSearcherConfig) => {
        if (id === TabType.TRAINS && tripType === TripType.ONE_WAY) {
            updateSearcherConfig(searcherConfigs.trains);
        }
    };

    const updateConfigForFlights = (id, tripType, updateSearcherConfig) => {
        if (id === TabType.FLIGHTS) {
            updateSearcherConfig(searcherConfigs.default);
        }
    };

    const setRoundTripForTrains = (id, tripType, setTripType) => {
        if (id === TabType.TRAINS && (tripType === TripType.MULTIPLE || tripType === TripType.ONE_WAY)) {
            setTripType(TripType.ROUND_TRIP);
        }
    };

    const handleTripTypeAndConfig = (id, tripType, setTripType, updateSearcherConfig) => {
        setRoundTripIfNeeded(id, tripType, setTripType, updateSearcherConfig);
        updateConfigForTrains(id, tripType, updateSearcherConfig);
        updateConfigForFlights(id, tripType, updateSearcherConfig);
        setRoundTripForTrains(id, tripType, setTripType);
    };

    const handleHotelAndStay = (id, setHotel, setSeparateStay) => {
        setHotel(id === TabType.DP || id === TabType.HOTELS);
        setSeparateStay({ enabled: false });
    };

    const handleSegmentSanitization = (id, segments, updateSegment, hasToSanitizeSegments) => {
        if (hasToSanitizeSegments(id)) {
            const segmentSanitized = {
                ...segments[0],
                destination: null,
            };
            updateSegment(segmentSanitized, segments[0].id);
        }
    };

    const onClickTab = (id, manualClick = true) => {
        const {
            setActiveProduct,
            setHotel,
            setSeparateStay,
            setTripType,
            tripType,
            updateSegment,
            updateSearcherConfig,
            segments,
            showErrors,
        } = props;

        hideDealsWidget(id);
        hideLoginWidgets(id);

        if (id !== previousId?.current) {
            previousId.current = id;
            setActiveProduct(id);
            setSelectedTab(id);

            handleTripTypeAndConfig(id, tripType, setTripType, updateSearcherConfig);
            handleHotelAndStay(id, setHotel, setSeparateStay);
            handleSegmentSanitization(id, segments, updateSegment, hasToSanitizeSegments);

            showErrors(false);
            manualClick && trackTabSelection(id);
        }
    };

    const trackTabSelection = (id) => {
        const { trackEvent } = props;
        const action = TrackingUtils.ACTION.PRODUCT_SELECTOR;
        let label = TrackingUtils.LABEL.productSelector(id);

        if (id === TabType.CARS || id === TabType.HOTELS) {
            if (iframeContext.isPrimeBenefits && isCarsForPrime && id === TabType.CARS) {
                label += ':prime_pag:home';
            } else if (iframeContext.isPrimeBenefits && isHotelsForPrime && id === TabType.HOTELS) {
                label += ':prime_pag:home';
            } else {
                label += ':no-prime_pag:home';
            }
        }

        trackEvent({ action, label });
    };

    hideLoginWidgets(selectedTab);

    return (
        <div>
            <div className="odf-tabs-sm">
                {getTabsToRender().map((tabContent) => (
                    <Tab
                        {...tabContent}
                        isActive={tabContent.id === selectedTab}
                        key={tabContent.id}
                        onClickTab={onClickTab}
                        tabIds={tabIds}
                    />
                ))}
            </div>
            <div className="odf-tabs-panel odf-tabs-manager-content" data-testid={`searcher-active-${selectedTab}`}>
                {renderTabContent()}
                <div style={{ display: selectedTab === TabType.CARS ? 'block' : 'none' }}>
                    <ErrorBoundary key={TabType.CARS}>
                        {iframeContext.isPrimeBenefits && isCarsForPrime && (
                            <PrimeBenefitBanner
                                bannerTextKey={`prime.home.searchbox.${TabType.CARS}.discount.banner`}
                                classAttribute={'odf-space-outer-left-l'}
                                pillCmsKey={'prime.home.searchbox.pill.new'}
                            />
                        )}
                        {iframeContext.isPrimeBanner && isCarsForPrime && (
                            <PrimeBanner tabType={TabType.CARS} />
                        )}
                        <IframeListener listener={carsIframeListener}>
                            <IframeProductManager
                                product={TabType.CARS}
                                height="330px"
                                appContext={iframeContext}
                                url={carUrl}
                            />
                        </IframeListener>
                    </ErrorBoundary>
                </div>
            </div>
        </div>
    );
};


TabManager.propTypes = {
    activeTab: TabTypeProp,
    trackEvent: PropTypes.func.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    entryPoint: EntryPointProp,
    tripType: TripTypeProp,
    rootDataAttr: PropTypes.object,
    separateStay: PropTypes.shape({
        enabled: PropTypes.bool,
        checkInDate: PropTypes.instanceOf(Date),
        checkOutDate: PropTypes.instanceOf(Date),
    }),
    segments: PropTypes.arrayOf(SearchSegment),
    showErrors: PropTypes.func.isRequired,
    setActiveProduct: PropTypes.func.isRequired,
    setHotel: PropTypes.func.isRequired,
    setTripType: PropTypes.func.isRequired,
    updateSegment: PropTypes.func.isRequired,
    updateSearcherConfig: PropTypes.func,
    setSeparateStay: PropTypes.func.isRequired,
    wmpProductList: PropTypes.string,
};

TabManager.defaultProps = {
    activeTab: TabTypePropDefault,
    entryPoint: EntryPointPropDefault,
    tripType: TripTypePropDefault,
    separateStay: {
        enabled: false,
    },
    segments: [],
    wmpProductList: '',
    updateSearcherConfig: ()=>{},
};

const mapStateToProps = ({ activeProduct, currentSearch }) => ({
    activeTab: activeProduct.activeProduct,
    tripType: currentSearch.tripType,
    segments: currentSearch.segments,
    separateStay: currentSearch.separateStay,
});

const ConnectedTabManager = connect(mapStateToProps, {
    setActiveProduct,
    setHotel,
    setTripType,
    updateSegment,
    updateSearcherConfig,
    setSeparateStay,
    showErrors,
})(withTranslation()(withPageContext(TabManager)));

export { ConnectedTabManager };
